// Job Description Input Step
import React, { useState } from 'react';
import { FileText, Lightbulb, Copy, Check } from 'lucide-react';

interface JobDescriptionStepProps {
  jobDescription: string;
  onJobDescriptionChange: (jobDescription: string) => void;
}

const sampleJobDescription = `Senior Software Engineer - Full Stack

About the Role:
We are looking for a Senior Software Engineer to join our growing engineering team. You will be responsible for designing, developing, and maintaining scalable web applications using modern technologies.

Key Responsibilities:
• Design and develop full-stack web applications using React, Node.js, and cloud technologies
• Collaborate with cross-functional teams to define, design, and ship new features
• Write clean, maintainable, and efficient code
• Participate in code reviews and maintain high code quality standards
• Mentor junior developers and contribute to team knowledge sharing
• Work with product managers to translate business requirements into technical solutions

Required Qualifications:
• Bachelor's degree in Computer Science or related field
• 5+ years of experience in software development
• Strong proficiency in JavaScript, TypeScript, React, and Node.js
• Experience with cloud platforms (AWS, Azure, or GCP)
• Knowledge of database systems (SQL and NoSQL)
• Experience with version control systems (Git)
• Strong problem-solving skills and attention to detail

Preferred Qualifications:
• Experience with microservices architecture
• Knowledge of DevOps practices and CI/CD pipelines
• Experience with containerization (Docker, Kubernetes)
• Previous experience in a fast-paced startup environment`;

export const JobDescriptionStep: React.FC<JobDescriptionStepProps> = ({
  jobDescription,
  onJobDescriptionChange
}) => {
  const [showSample, setShowSample] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleUseSample = () => {
    onJobDescriptionChange(sampleJobDescription);
    setShowSample(false);
  };

  const handleCopyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(sampleJobDescription);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy to clipboard:', err);
    }
  };

  const wordCount = jobDescription.trim().split(/\s+/).filter(word => word.length > 0).length;
  const minWords = 50;
  const isValidLength = wordCount >= minWords;

  return (
    <div className="space-y-6">
      <div className="text-center">
        <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <FileText className="w-8 h-8 text-primary-600" />
        </div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          Job Description
        </h2>
        <p className="text-gray-600">
          Paste the job description for the position you're interviewing for. This helps the AI provide relevant, targeted responses.
        </p>
      </div>

      <div className="max-w-2xl mx-auto space-y-4">
        <div>
          <label htmlFor="jobDescription" className="block text-sm font-medium text-gray-700 mb-2">
            Job Description *
          </label>
          <textarea
            id="jobDescription"
            value={jobDescription}
            onChange={(e) => onJobDescriptionChange(e.target.value)}
            placeholder="Paste the job description here..."
            className="textarea-field min-h-[300px]"
            autoFocus
          />
          <div className="flex justify-between items-center mt-2">
            <p className={`text-sm ${isValidLength ? 'text-gray-500' : 'text-orange-600'}`}>
              {wordCount} words {!isValidLength && `(minimum ${minWords} required)`}
            </p>
            <button
              onClick={() => setShowSample(!showSample)}
              className="text-primary-600 hover:text-primary-700 text-sm font-medium flex items-center"
            >
              <Lightbulb className="w-4 h-4 mr-1" />
              {showSample ? 'Hide' : 'Show'} Sample
            </button>
          </div>
        </div>

        {showSample && (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <div className="flex justify-between items-center mb-3">
              <h3 className="font-medium text-gray-900">Sample Job Description</h3>
              <div className="flex space-x-2">
                <button
                  onClick={handleCopyToClipboard}
                  className="flex items-center px-3 py-1 text-sm text-gray-600 hover:text-gray-800 border border-gray-300 rounded hover:bg-gray-100 transition-colors"
                >
                  {copied ? (
                    <>
                      <Check className="w-3 h-3 mr-1" />
                      Copied
                    </>
                  ) : (
                    <>
                      <Copy className="w-3 h-3 mr-1" />
                      Copy
                    </>
                  )}
                </button>
                <button
                  onClick={handleUseSample}
                  className="px-3 py-1 text-sm bg-primary-600 text-white rounded hover:bg-primary-700 transition-colors"
                >
                  Use This Sample
                </button>
              </div>
            </div>
            <div className="text-sm text-gray-700 whitespace-pre-line max-h-40 overflow-y-auto">
              {sampleJobDescription}
            </div>
          </div>
        )}

        {jobDescription && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start">
              <Lightbulb className="w-5 h-5 text-blue-600 mr-3 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-blue-800 font-medium text-sm">AI Analysis Preview</p>
                <p className="text-blue-700 text-xs mt-1">
                  The AI will analyze this job description to understand the role requirements, company culture, and key skills needed. This information will be used to craft personalized interview responses that highlight your relevant experience.
                </p>
              </div>
            </div>
          </div>
        )}

        {!isValidLength && jobDescription.trim().length > 0 && (
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
            <div className="flex items-start">
              <FileText className="w-5 h-5 text-orange-600 mr-3 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-orange-800 font-medium text-sm">More Details Needed</p>
                <p className="text-orange-700 text-xs mt-1">
                  Please provide a more detailed job description. Include responsibilities, requirements, and qualifications for better AI assistance.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
